if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        }
      }

      connectedCallback() {
        this.dataset.preventWindowScroll = 'true';
        const sectionId = this.dataset.sectionId || (this.id ? this.id.replace('MediaGallery-', '') : '');
        const selector = sectionId
          ? `variant-selects[data-section="${sectionId}"] input[name*="Color"]:checked, ` +
            `variant-selects[data-section="${sectionId}"] input[name*="color"]:checked, ` +
            `variant-selects[data-section="${sectionId}"] input[name*="Metal"]:checked, ` +
            `variant-selects[data-section="${sectionId}"] input[name*="metal"]:checked, ` +
            `variant-selects[data-section="${sectionId}"] select[name*="Color"], ` +
            `variant-selects[data-section="${sectionId}"] select[name*="color"]`
          : 'variant-selects input[name*="Color"]:checked, variant-selects input[name*="color"]:checked, variant-selects select[name*="Color"]';

        const initialColorInput = document.querySelector(selector);
        if (initialColorInput && initialColorInput.value) {
          this.filterMediaByColor(initialColorInput.value);
        } else {
          this.ensureVideoSecond();
        }
      }

      ensureVideoSecond(firstColorMatch) {
        // Viewer slides
        const slides = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]:not(.is-hidden)') || []);
        if (slides.length >= 2) {
          const firstSlide =
            firstColorMatch ||
            slides.find((s) => s.dataset.mediaType !== 'video' && s.dataset.mediaType !== 'external_video') ||
            slides[0];
          const videoSlide = slides.find(
            (s) => s.dataset.mediaType === 'video' || s.dataset.mediaType === 'external_video'
          );

          if (firstSlide && videoSlide && firstSlide !== videoSlide) {
            firstSlide.after(videoSlide);
          }
        }

        // Thumbnails
        if (this.elements.thumbnails) {
          const thumbs = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]:not(.is-hidden)') || []);
          if (thumbs.length >= 2) {
            const firstThumb =
              thumbs.find((t) => t.dataset.mediaType !== 'video' && t.dataset.mediaType !== 'external_video') ||
              thumbs[0];
            const videoThumb = thumbs.find(
              (t) => t.dataset.mediaType === 'video' || t.dataset.mediaType === 'external_video'
            );

            if (firstThumb && videoThumb && firstThumb !== videoThumb) {
              firstThumb.after(videoThumb);
            }
          }
          if (this.elements.thumbnails.resetPages) {
            this.elements.thumbnails.resetPages();
          }
        }

        // Mobile Dots
        const dotsWrap = this.querySelector('[id^="GalleryDots"]');
        if (dotsWrap) {
          const dots = Array.from(dotsWrap.querySelectorAll('.slider-dot:not(.is-hidden)'));
          if (dots.length >= 2) {
            const firstDot =
              dots.find((d) => d.dataset.mediaType !== 'video' && d.dataset.mediaType !== 'external_video') ||
              dots[0];
            const videoDot = dots.find(
              (d) => d.dataset.mediaType === 'video' || d.dataset.mediaType === 'external_video'
            );

            if (firstDot && videoDot && firstDot !== videoDot) {
              firstDot.after(videoDot);
            }
          }
        }

        // Autoplay all videos muted without controls
        this.querySelectorAll('video').forEach((v) => {
          v.muted = true;
          v.controls = false;
          v.setAttribute('playsinline', '');
          v.setAttribute('autoplay', '');
          v.setAttribute('loop', '');
          if (v.paused) {
            v.play().catch(() => {});
          }
        });
      }

      filterMediaByColor(colorName) {
        if (!colorName) return;
        const targetColor = colorName.trim().toLowerCase();

        // 1. Filter Viewer Slides
        const slides = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        if (!slides.length) return;

        let firstColorMatch = null;
        let firstAnyMatch = null;
        let visibleCount = 0;

        slides.forEach((slide) => {
          const slideColor = (slide.dataset.color || '').trim().toLowerCase();
          const isVideo =
            slide.dataset.mediaType === 'video' ||
            slide.dataset.mediaType === 'external_video' ||
            Boolean(slide.querySelector('video, deferred-media, [data-shopify-editor-video]'));
          const isColorMatch = slideColor === targetColor;
          const isMatch = isVideo || slideColor === 'all' || isColorMatch;

          if (isMatch) {
            slide.classList.remove('is-hidden');
            visibleCount++;
            if (!firstAnyMatch) firstAnyMatch = slide;
            if (!firstColorMatch && isColorMatch && !isVideo) firstColorMatch = slide;
          } else {
            slide.classList.add('is-hidden');
            slide.classList.remove('is-active');
          }
        });

        // Fallback: If no slides matched the specific color, unhide all
        if (visibleCount === 0) {
          slides.forEach((slide) => slide.classList.remove('is-hidden'));
          firstAnyMatch = slides[0];
          visibleCount = slides.length;
        }

        // Toggle single modifier if only 1 slide visible
        slides.forEach((slide) => {
          slide.classList.toggle('product__media-item--single', visibleCount === 1);
        });

        // 2. Filter Thumbnails
        if (this.elements.thumbnails) {
          const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]') || []);
          thumbnails.forEach((thumb) => {
            const thumbColor = (thumb.dataset.color || '').trim().toLowerCase();
            const isVideo =
              thumb.dataset.mediaType === 'video' ||
              thumb.dataset.mediaType === 'external_video' ||
              Boolean(thumb.querySelector('.thumbnail__badge'));
            const isMatch = visibleCount === 0 || isVideo || thumbColor === 'all' || thumbColor === targetColor;
            thumb.classList.toggle('is-hidden', !isMatch);
          });
        }

        // 3. Filter Mobile Dots
        const dotsWrap = this.querySelector('[id^="GalleryDots"]');
        if (dotsWrap) {
          const dots = Array.from(dotsWrap.querySelectorAll('.slider-dot'));
          dots.forEach((dot) => {
            const dotColor = (dot.dataset.color || '').trim().toLowerCase();
            const isVideo = dot.dataset.mediaType === 'video' || dot.dataset.mediaType === 'external_video';
            const isMatch = visibleCount === 0 || isVideo || dotColor === 'all' || dotColor === targetColor;
            dot.classList.toggle('is-hidden', !isMatch);
            dot.classList.remove('is-active');
            dot.setAttribute('aria-selected', 'false');
          });
        }

        // 4. Filter Modal (Lightbox)
        const sectionId = this.dataset.sectionId || (this.id ? this.id.replace('MediaGallery-', '') : '');
        const modal = document.querySelector(`product-modal#ProductModal-${sectionId}`) || document.querySelector('product-modal');
        if (modal) {
          const modalItems = Array.from(modal.querySelectorAll('[data-media-id]'));
          let firstColorModal = null;
          let videoModal = null;

          modalItems.forEach((item) => {
            const itemColor = (item.dataset.color || '').trim().toLowerCase();
            const isVideo =
              item.dataset.mediaType === 'video' ||
              item.dataset.mediaType === 'external_video' ||
              item.matches('deferred-media, video');
            const isColorMatch = itemColor === targetColor;
            const isMatch = visibleCount === 0 || isVideo || itemColor === 'all' || isColorMatch;
            item.classList.toggle('is-hidden', !isMatch);

            if (isMatch) {
              if (!firstColorModal && isColorMatch && !isVideo) firstColorModal = item;
              if (!videoModal && isVideo) videoModal = item;
            }
          });

          if (firstColorModal && videoModal && firstColorModal !== videoModal) {
            firstColorModal.after(videoModal);
          }
        }

        // 5. Position the video as 2nd item across slides, thumbnails, and dots!
        this.ensureVideoSecond(firstColorMatch);

        // 6. Activate first matching color slide and reset viewer slider immediately
        const activeTarget = firstColorMatch || firstAnyMatch;
        if (activeTarget) {
          slides.forEach((s) => s.classList.remove('is-active'));
          activeTarget.classList.add('is-active');

          if (this.elements.viewer?.slider) {
            if (typeof this.elements.viewer.resetPages === 'function') {
              this.elements.viewer.resetPages();
            }
            this.elements.viewer.slider.scrollTo({ left: 0, behavior: 'instant' });
          }

          if (this.elements.thumbnails) {
            const activeThumb = this.elements.thumbnails.querySelector(`[data-target="${activeTarget.dataset.mediaId}"]`);
            if (activeThumb) {
              this.setActiveThumbnail(activeThumb);
            }
          }

          if (dotsWrap) {
            const activeDot = dotsWrap.querySelector(`[data-target="${activeTarget.dataset.mediaId}"]`);
            if (activeDot) {
              activeDot.classList.add('is-active');
              activeDot.setAttribute('aria-selected', 'true');
            }
          }
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not(.is-hidden)`) ||
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]:not(.is-hidden)') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        // Only prepend if not using color filtering (to keep natural order of images intact)
        const hasColorFiltering = this.querySelector('[data-color]');
        if (prepend && !hasColorFiltering) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });
          }
          // Never scroll the whole browser window when switching variants
          if (this.dataset.preventWindowScroll === 'true') return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
